import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Upload, FileSpreadsheet } from 'lucide-react';

export default function DesktopImport({ base, isDark = true }) {
  const [dry, setDry] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const journalRef = useRef(null);
  const detailRef = useRef(null);
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const panel = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';

  const files = () => {
    const j = journalRef.current?.files?.[0];
    const d = detailRef.current?.files?.[0];
    return { j, d };
  };

  const run = async (mode) => {
    const { j, d } = files();
    if (!j || !d) { setError('Choose both the Journal CSV and the Transaction Detail CSV'); return; }
    setError(''); setBusy(mode);
    const fd = new FormData();
    fd.append('journal', j); fd.append('detail', d);
    try {
      const res = await axios.post(`${base}/import/desktop/${mode}`, fd);
      if (mode === 'dry-run') { setDry(res.data); setCommitted(null); }
      else { setCommitted(res.data); }
    } catch (e) { setError(e?.response?.data?.detail || `${mode} failed`); }
    finally { setBusy(''); }
  };

  const Summary = ({ data, title }) => (
    <div className="text-sm space-y-2" data-testid="desktop-summary">
      <div className={sub}>{title}: <span className="text-emerald-500 font-semibold">{data.posted}</span> posted
        {' · '}{data.total} total{data.skipped_dup ? ` · ${data.skipped_dup} already imported` : ''}
        {data.ar_applications ? ` · ${data.ar_applications} A/R links` : ''}
        {data.ap_applications ? ` · ${data.ap_applications} A/P links` : ''}</div>
      <table className="w-full">
        <thead><tr className={`text-left ${sub}`}><th className="p-1">Type</th><th className="p-1 text-right">Count</th></tr></thead>
        <tbody>
          {Object.entries(data.by_type || {}).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
            <tr key={t}><td className="p-1">{t}</td><td className="p-1 text-right tabular-nums">{n}</td></tr>
          ))}
        </tbody>
      </table>
      {(data.needs_review || []).length > 0 && (
        <div className="rounded border border-amber-500/50 bg-amber-500/10 p-2" data-testid="desktop-needs-review">
          <div className="text-amber-500 font-medium">{data.needs_review.length} need review</div>
          {data.needs_review.slice(0, 8).map((r, i) => (
            <div key={i} className={sub}>#{r.trans_no}: {r.reason}</div>
          ))}
        </div>
      )}
      {(data.unknown_type || []).length > 0 && (
        <div className={sub}>{data.unknown_type.length} unknown-type rows skipped</div>
      )}
    </div>
  );

  return (
    <Card className={panel} data-testid="olfin-desktop-import">
      <CardHeader className="pb-2">
        <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <FileSpreadsheet className="h-4 w-4 text-emerald-500" /> desktop accounting — transaction import
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className={`text-sm ${sub}`}>
          Upload the OL Finance <b>Journal</b> report and the <b>Transaction Detail by Account</b> report
          (both as CSV), preview with a dry-run, then commit. Re-running is safe — already-imported
          transactions are skipped.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1"><span className={`text-xs ${sub}`}>Journal CSV</span>
            <input type="file" accept=".csv" ref={journalRef} className={`block text-sm ${sub}`} data-testid="desktop-journal-file" /></label>
          <label className="space-y-1"><span className={`text-xs ${sub}`}>Transaction Detail by Account CSV</span>
            <input type="file" accept=".csv" ref={detailRef} className={`block text-sm ${sub}`} data-testid="desktop-detail-file" /></label>
        </div>
        {error && <div className="rounded border border-red-500/50 bg-red-500/10 p-2 text-sm text-red-400" data-testid="desktop-error">{error}</div>}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => run('dry-run')} disabled={!!busy} data-testid="desktop-dryrun-btn">
            {busy === 'dry-run' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Dry-run</>}
          </Button>
          {dry && (
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => run('commit')} disabled={!!busy} data-testid="desktop-commit-btn">
              {busy === 'commit' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Commit import'}
            </Button>
          )}
        </div>
        {dry && !committed && <Summary data={dry} title="Dry-run" />}
        {committed && <Summary data={committed} title="Imported" />}
      </CardContent>
    </Card>
  );
}
